import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  FileText, CheckCircle, Clock, Eye, AlertCircle, Award, ThumbsUp,
  Lock, DollarSign, FileSignature, Target, LayoutDashboard,
  ExternalLink, User, Mail, Building2, ChevronDown, ChevronUp,
  MessageSquare
} from 'lucide-react';
import { Button } from '../ui/button';
import BrokerToolkit from '../toolkit/BrokerToolkit';
import MessagesAndDocuments from '../applications/MessagesAndDocuments';
import DocumentUploader from '../shared/DocumentUploader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ------------------------------------------------------------------ */
/*  Progress Timeline Steps                                           */
/* ------------------------------------------------------------------ */
const TIMELINE_STEPS = [
  { key: 'submitted', label: 'Submitted', icon: Clock },
  { key: 'submission_complete', label: 'Submission Complete', icon: CheckCircle },
  { key: 'prequalified', label: 'Prequalified', icon: Award },
  { key: 'borrower_accepts_offer', label: 'Accepted', icon: ThumbsUp },
  { key: 'under_review', label: 'Under Review', icon: Eye },
  { key: 'pending_docs', label: 'Pending Docs/Info', icon: AlertCircle },
  { key: 'approved', label: 'Approved', icon: CheckCircle },
  { key: 'closing', label: 'Closing', icon: FileSignature },
  { key: 'closed', label: 'Closed', icon: Lock },
  { key: 'funded', label: 'Funded', icon: DollarSign },
];

const getStepIndex = (status) => {
  const idx = TIMELINE_STEPS.findIndex(s => s.key === status);
  return idx >= 0 ? idx : 0;
};

/* ------------------------------------------------------------------ */
/*  ApplicationStatusView                                             */
/* ------------------------------------------------------------------ */
const ApplicationStatusView = ({ app, onOpenMessages, onDocUploaded }) => {
  const currentIdx = getStepIndex(app.status);
  const [expanded, setExpanded] = useState(true);

  const productLabel = (app.product_type || '').replace(/_/g, ' ').toUpperCase();

  const statusBadgeStyle = () => {
    const s = app.status;
    if (s === 'funded') return 'bg-teal-100 text-teal-800 border-teal-200';
    if (s === 'approved') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (s === 'submission_complete') return 'bg-sky-100 text-sky-800 border-sky-200';
    if (s === 'pending_docs') return 'bg-orange-100 text-orange-800 border-orange-200';
    if (s === 'under_review') return 'bg-amber-100 text-amber-800 border-amber-200';
    if (s === 'declined') return 'bg-red-100 text-red-800 border-red-200';
    if (s === 'closing' || s === 'closed') return 'bg-violet-100 text-violet-800 border-violet-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const statusLabel = () => {
    const cfg = TIMELINE_STEPS.find(t => t.key === app.status);
    return cfg?.label || app.status;
  };

  return (
    <div className="space-y-4" data-testid={`app-status-${app.id}`}>
      {/* Application Header */}
      <div className="bg-[#081120] rounded-xl p-5 text-white" data-testid="app-header">
        <div className="flex items-center gap-3 mb-1">
          <FileText className="h-5 w-5 text-slate-300" />
          <h3 className="text-base font-bold">Application Status</h3>
        </div>
        <p className="text-sm text-slate-400 ml-8">Track the progress of your loan application</p>
      </div>

      {/* Application Info Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="app-info-card">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-800">{app.business_name}</h2>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-xs font-semibold">{productLabel}</Badge>
              <Badge className={`border text-xs font-semibold ${statusBadgeStyle()}`}>
                {app.status === 'funded' && <DollarSign className="h-3 w-3 mr-0.5" />}
                {statusLabel()}
              </Badge>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Requested Amount</p>
            <p className="text-2xl font-bold text-emerald-600">${Number(app.amount_requested || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Messages Button */}
        <div className="mt-4 flex gap-2">
          <Button size="sm" variant="outline" className="text-slate-700 border-slate-300" onClick={onOpenMessages} data-testid={`messages-btn-${app.id}`}>
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Messages & Documents
          </Button>
        </div>
      </div>

      {/* Progress Timeline */}
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="progress-timeline">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-4 w-4 text-slate-600" />
          <h4 className="text-sm font-bold text-slate-800">Progress</h4>
        </div>
        <div className="relative pl-4">
          {TIMELINE_STEPS.map((step, i) => {
            const isCompleted = i <= currentIdx;
            const isCurrent = i === currentIdx;
            const isDeclined = app.status === 'declined' && step.key === 'declined';
            const StepIcon = step.icon;

            // Skip declined steps in the normal flow
            if (step.key === 'borrower_declines_offer') return null;

            return (
              <div key={step.key} className="flex items-start gap-3 relative" data-testid={`timeline-step-${step.key}`}>
                {/* Vertical line */}
                {i < TIMELINE_STEPS.length - 1 && (
                  <div className={`absolute left-[13px] top-[28px] w-0.5 h-[calc(100%-4px)] ${isCompleted && i < currentIdx ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                )}
                {/* Circle */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                  isCurrent
                    ? 'bg-[#0E333F] text-white'
                    : isCompleted
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-100 text-slate-400 border border-slate-200'
                }`}>
                  {isCompleted && !isCurrent ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : isCurrent ? (
                    <span className="text-xs font-bold">{i + 1}</span>
                  ) : (
                    <span className="text-[10px] font-medium">{i + 1}</span>
                  )}
                </div>
                {/* Label */}
                <div className={`pb-5 ${isCurrent ? 'font-semibold text-slate-800' : isCompleted ? 'text-slate-700' : 'text-slate-400'}`}>
                  <span className="text-sm">{step.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Document Upload Section */}
      {(app.required_documents || []).length > 0 && (
        <DocumentUploader
          applicationId={app.id}
          requiredDocuments={app.required_documents}
          authToken={localStorage.getItem('auth_token')}
          debtSchedule={app.debt_schedule || []}
          debtSchedulePdfUrl={app.debt_schedule_pdf_url}
          onUploadComplete={() => { if (onDocUploaded) onDocUploaded(); }}
          onDebtScheduleSave={() => { if (onDocUploaded) onDocUploaded(); }}
        />
      )}

      {/* Document Folder Link */}
      {app.document_folder_url && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3" data-testid="doc-folder-link">
          <ExternalLink className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <div>
            <span className="text-sm text-slate-600">Document folder: </span>
            <a href={app.document_folder_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
              {app.document_folder_url}
            </a>
          </div>
        </div>
      )}

      {/* Broker Info */}
      {app.broker && (
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="broker-info">
          <div className="flex items-center gap-2 mb-3">
            <User className="h-4 w-4 text-slate-500" />
            <h4 className="text-sm font-bold text-slate-700">Your Broker</h4>
          </div>
          <div className="ml-6 space-y-1">
            <p className="text-sm font-semibold text-slate-800">{app.broker.name}</p>
            {app.broker.company_name && (
              <p className="text-sm text-slate-500 flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" /> {app.broker.company_name}
              </p>
            )}
            {app.broker.email && (
              <a href={`mailto:${app.broker.email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> {app.broker.email}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-right text-xs text-slate-400 px-1" data-testid="app-footer">
        Applied {new Date(app.created_at).toLocaleDateString()}
        {app.updated_at && <> &middot; Last updated {new Date(app.updated_at).toLocaleDateString()}</>}
      </div>
    </div>
  );
};


/* ================================================================== */
/*  Main: BorrowerDashboard                                           */
/* ================================================================== */
const BorrowerDashboard = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('applications');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showMessages, setShowMessages] = useState(null);
  const token = localStorage.getItem('auth_token');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API}/borrower/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(data || []);
      if (data.length > 0) setSelectedApp(data[0]);
    } catch (error) {
      console.error('Error fetching borrower apps:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="borrower-dashboard">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Welcome, {user.name}!</h2>
        <p className="text-slate-600">Track your applications and improve your loan readiness</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="applications" className="flex items-center gap-2" data-testid="tab-applications">
            <LayoutDashboard className="h-4 w-4" /> My Applications
          </TabsTrigger>
          <TabsTrigger value="workshop" className="flex items-center gap-2" data-testid="tab-workshop">
            <Target className="h-4 w-4" /> Professional Tools
          </TabsTrigger>
        </TabsList>

        {/* Applications Tab */}
        <TabsContent value="applications">
          {applications.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <FileText className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">No applications found</h3>
              <p className="text-slate-600">Contact your broker to get started with a loan application.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* App Selector (if multiple) */}
              {applications.length > 1 && (
                <div className="flex items-center gap-2 flex-wrap">
                  {applications.map(app => (
                    <Button key={app.id} size="sm"
                      variant={selectedApp?.id === app.id ? 'default' : 'outline'}
                      className={selectedApp?.id === app.id ? 'bg-[#0E333F] text-white' : 'text-slate-700 border-slate-300'}
                      onClick={() => setSelectedApp(app)}
                      data-testid={`select-app-${app.id}`}
                    >
                      {app.business_name}
                    </Button>
                  ))}
                </div>
              )}

              {/* Application Status View */}
              {selectedApp && (
                <ApplicationStatusView
                  app={selectedApp}
                  onOpenMessages={() => setShowMessages(selectedApp)}
                  onDocUploaded={fetchData}
                />
              )}
            </div>
          )}
        </TabsContent>

        {/* Professional Tools Tab */}
        <TabsContent value="workshop">
          <BrokerToolkit />
        </TabsContent>
      </Tabs>

      {/* Messages Modal */}
      {showMessages && (
        <MessagesAndDocuments
          applicationId={showMessages.id}
          currentUserId={user.id}
          currentUserRole="borrower"
          onClose={() => setShowMessages(null)}
        />
      )}
    </div>
  );
};

export default BorrowerDashboard;
